const canonicalizeKktUrl = (url) =>
  String(url || '').replace(/\.new\.kktbel\.ru/gi, '.kktbel.ru');

const PROD_EO_FALLBACK = 'https://e-learn.kktbel.ru';
const LOCAL_EO_FALLBACK = 'http://127.0.0.1:8082';

const trimSlash = (url) => (url || '').replace(/\/$/, '');

export const isLoopbackHost = (hostname) => {
  const h = String(hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
};

export const isLoopbackUrl = (url) => {
  try {
    return isLoopbackHost(new URL(url).hostname);
  } catch (_) {
    return false;
  }
};

/** Старый FastAPI расписания. API теперь Django ЭО (локально :8082). */
const isStaleApiUrl = (url) => {
  try {
    return new URL(url).port === '8011';
  } catch (_) {
    return false;
  }
};

/** Страница открыта через публичный хост, не с localhost */
export const isBrowserOnPublicHost = () => {
  if (typeof window === 'undefined') return false;
  return !isLoopbackHost(window.location.hostname);
};

const isPublicScheduleHost = () => {
  if (typeof window === 'undefined') return false;
  const sub = (window.location.hostname.split('.')[0] || '').toLowerCase();
  return sub === 'schedule' || sub === 'shedule';
};

/** Loopback с публичного HTTPS браузер режет (Private Network Access). */
export const canUsePublicEoUrl = (url) => {
  if (!url) return false;
  if (isBrowserOnPublicHost() && isLoopbackUrl(url)) return false;
  return true;
};

const isTrustedApiHost = (hostname) => {
  const h = String(hostname || '').toLowerCase();
  if (isLoopbackHost(h)) return true;
  if (h === 'kktbel.ru' || h.endsWith('.kktbel.ru')) return true;
  if (h.endsWith('.cloudpub.ru')) return true;
  return false;
};

const canUseApiUrl = (url) => {
  if (!url || isStaleApiUrl(url)) return false;
  if (!canUsePublicEoUrl(url)) return false;
  // На публичном хосте — только наши API; локально оставляем свободнее для отладки.
  if (!isBrowserOnPublicHost()) return true;
  try {
    return isTrustedApiHost(new URL(url).hostname);
  } catch (_) {
    return false;
  }
};

const readRuntimeApiUrl = () => {
  try {
    const fromStorage = trimSlash(localStorage.getItem('kkt_api_url') || '');
    if (fromStorage && isStaleApiUrl(fromStorage)) {
      localStorage.removeItem('kkt_api_url');
    } else if (canUseApiUrl(fromStorage)) {
      return fromStorage;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    const fromConfig = trimSlash(window.__KKT_CONFIG__?.API_URL || '');
    if (canUseApiUrl(fromConfig)) return fromConfig;
  } catch (_) {
    /* ignore */
  }

  return '';
};

export const getApiBaseUrl = () => {
  const runtime = readRuntimeApiUrl();
  if (canUseApiUrl(runtime)) {
    return canonicalizeKktUrl(runtime);
  }

  const fromEnv = trimSlash(process.env.REACT_APP_API_URL || '');
  if (canUseApiUrl(fromEnv)) {
    return canonicalizeKktUrl(fromEnv);
  }

  // Production schedule: nginx is static-only, same-origin '' won't reach EO.
  if (isBrowserOnPublicHost()) {
    if (isPublicScheduleHost()) {
      return PROD_EO_FALLBACK;
    }
    return '';
  }

  return LOCAL_EO_FALLBACK;
};

export const API_BASE = getApiBaseUrl();

export const apiUrl = (path = '') => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${getApiBaseUrl()}${normalized}`;
};

/** Для отладки: вручную задать API и перезагрузить */
export const setApiBaseUrlOverride = (url) => {
  const value = trimSlash(url || '');
  if (!value) {
    localStorage.removeItem('kkt_api_url');
    return;
  }
  if (!canUseApiUrl(value)) return;
  try {
    if (isBrowserOnPublicHost() && !isTrustedApiHost(new URL(value).hostname)) return;
  } catch (_) {
    return;
  }
  localStorage.setItem('kkt_api_url', value);
};
